import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, saveBlob } from '../../api/http';
import {
  Notice,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import {
  CERT_TYPES,
  type CertType,
  type CertificateRow,
  type DocumentRow,
  type Message,
  type RequestRow,
} from '../documents/shared';

/** A parent's view of one child's paperwork: documents the school shares, issued certificates and requests. */
export function ChildDocuments({ studentId }: { studentId: string }) {
  const queryClient = useQueryClient();
  const root = `/me/children/${studentId}`;
  const [type, setType] = useState<CertType>('bonafide');
  const [purpose, setPurpose] = useState('');
  const [message, setMessage] = useState<Message>(null);

  const docs = useQuery({
    queryKey: ['me', 'children', studentId, 'documents'],
    queryFn: () => api<DocumentRow[]>(`${root}/documents`),
  });
  const certs = useQuery({
    queryKey: ['me', 'children', studentId, 'certificates'],
    queryFn: () => api<CertificateRow[]>(`${root}/certificates`),
  });
  const requests = useQuery({
    queryKey: ['me', 'children', studentId, 'certificate-requests'],
    queryFn: () => api<RequestRow[]>(`${root}/certificate-requests`),
  });
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['me', 'children', studentId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });

  const ask = useMutation({
    mutationFn: () =>
      api(`${root}/certificate-requests`, { method: 'POST', body: { type, purpose } }),
    onSuccess: () => {
      setPurpose('');
      setMessage({ kind: 'ok', text: t('cert.requestSent') });
      refresh();
    },
    onError: fail,
  });
  const withdraw = useMutation({
    mutationFn: (id: string) =>
      api(`${root}/certificate-requests/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });

  const file = async (d: DocumentRow) => {
    try {
      saveBlob(await apiBlob(`${root}/documents/${d.id}/file`), d.originalName);
    } catch (e) {
      fail(e);
    }
  };
  const pdf = async (c: CertificateRow) => {
    try {
      saveBlob(
        await apiBlob(`${root}/certificates/${c.id}/certificate.pdf`),
        `${c.certificateNumber}.pdf`,
      );
    } catch (e) {
      fail(e);
    }
  };

  if (docs.isPending || certs.isPending) return <p role="status">{t('common.loading')}</p>;
  if (docs.isError) return <Notice kind="error">{errorMessage(docs.error)}</Notice>;

  return (
    <div className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <section aria-label={t('child.documents')} className="space-y-2">
        <h2 className="text-lg font-semibold">{t('child.documents')}</h2>
        {docs.data.length === 0 && <p className="text-slate-600">{t('child.noDocuments')}</p>}
        <ul className="space-y-2 text-sm">
          {docs.data.map((d) => (
            <li
              key={d.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3"
            >
              <span>
                <strong>{d.title}</strong> · {t(`doc.cat.${d.category}` as MessageKey)}
              </span>
              <button
                type="button"
                className={secondaryButton}
                aria-label={`${t('doc.download')} ${d.title}`}
                onClick={() => void file(d)}
              >
                {t('doc.download')}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section aria-label={t('cert.mine')} className="space-y-2">
        <h2 className="text-lg font-semibold">{t('cert.mine')}</h2>
        {certs.data?.length === 0 && <p className="text-slate-600">{t('cert.noneMine')}</p>}
        <ul className="space-y-2 text-sm">
          {certs.data?.map((c) => (
            <li
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3"
            >
              <span>
                <strong>{t(`cert.type.${c.type}` as MessageKey)}</strong> · {c.certificateNumber} ·{' '}
                {c.issueDate} · {t(`cert.status.${c.status}` as MessageKey)}
              </span>
              <button
                type="button"
                className={secondaryButton}
                aria-label={`${t('cert.pdf')} ${c.certificateNumber}`}
                onClick={() => void pdf(c)}
              >
                {t('cert.pdf')}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section aria-label={t('cert.request')} className="space-y-3">
        <h2 className="text-lg font-semibold">{t('cert.request')}</h2>
        <form
          noValidate
          aria-label={t('cert.request')}
          onSubmit={(e) => {
            e.preventDefault();
            if (!purpose.trim()) return;
            setMessage(null);
            ask.mutate();
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3"
        >
          <div>
            <label htmlFor="req-type" className="block text-sm font-medium">
              {t('cert.type')}
            </label>
            <select
              id="req-type"
              className={selectClass}
              value={type}
              onChange={(e) => setType(e.target.value as CertType)}
            >
              {CERT_TYPES.map((c) => (
                <option key={c} value={c}>
                  {t(`cert.type.${c}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          <TextField
            label={t('cert.purpose')}
            value={purpose}
            maxLength={300}
            onChange={(e) => setPurpose(e.target.value)}
          />
          <div className="self-end">
            <button
              type="submit"
              className={primaryButton}
              disabled={ask.isPending || !purpose.trim()}
            >
              {t('cert.send')}
            </button>
          </div>
        </form>
        <h3 className="font-medium">{t('cert.myRequests')}</h3>
        {requests.data?.length === 0 && <p className="text-slate-600">{t('cert.noRequests')}</p>}
        <ul className="space-y-2 text-sm">
          {requests.data?.map((r) => (
            <li key={r.id} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {t(`cert.type.${r.type}` as MessageKey)} — {r.purpose}
                </span>
                <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
                  {t(`cert.req.status.${r.status}` as MessageKey)}
                </span>
              </div>
              {r.decisionNote && <p className="mt-1 text-slate-600">{r.decisionNote}</p>}
              {r.status === 'pending' && (
                <button
                  type="button"
                  className={`${secondaryButton} mt-2`}
                  disabled={withdraw.isPending}
                  onClick={() => withdraw.mutate(r.id)}
                >
                  {t('cert.withdraw')}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
